import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Box, IconButton } from '@mui/material';
import { FC, useState } from 'react';
import { CustomTableHeader } from './table-header';
import { CustomTableBody } from './table-body';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { DialogComponent } from 'components/dialog-component';
import { PeerGroupForm } from './form';
import { useGetCountryList } from 'api/hooks/country/useGetCountry';
import { useCreateCountry } from 'api/hooks/country/useCreateCountry';
import { useUpdateCountry } from 'api/hooks/country/useUpdateCountry';
import { CustomPagination } from 'components/shared/custom-pagination';
import { WithSpinner } from 'components/with-spinner';
import {
    Country,
    CreateCountry,
    UpdateCountry,
} from 'api/hooks/country/countryTypes';

type Props = {};

export const AdminCountryListTable: FC<Props> = () => {
    const [recordToCreateUpdate, setRecordToCreateUpdate] =
        useState<Country | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [isCreate, setIsCreate] = useState(false);

    const { data, isLoading, query: getList } = useGetCountryList({});

    const { mutate: create } = useCreateCountry({
        onSuccess: (data) => {
            getList.refetch();
            setFormOpen(false);
        },
    });

    const { mutate: update } = useUpdateCountry({
        onSuccess: (data) => {
            getList.refetch();
            setFormOpen(false);
        },
    });

    const handleOpenCreateFormDialog = () => {
        setRecordToCreateUpdate({} as Country);
        setFormOpen(true);
        setIsCreate(true);
    };

    const handleOpenEditFormDialog = (id: string) => {
        const foundRecord = data.find((record) => record._id === id);

        if (!foundRecord) return;

        setRecordToCreateUpdate(foundRecord);
        setFormOpen(true);
        setIsCreate(false);
    };

    const handleFormClose = () => {
        setFormOpen(false);
    };

    const handleFormSave = (data: CreateCountry | UpdateCountry) => {
        if (isCreate) {
            const dataToCreate = data as CreateCountry;
            create(dataToCreate);
        } else {
            const dataToUpdate = data as UpdateCountry;
            update({
                id: recordToCreateUpdate?._id ?? '',
                request: dataToUpdate,
            });
        }
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const pagedData =
        (data &&
            data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((element) => element)) ??
        [];

    return (
        <WithSpinner isLoading={isLoading}>
            <Box sx={{ width: '100%', ml: 1 }}>
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <IconButton onClick={handleOpenCreateFormDialog}>
                        <AddCircleIcon fontSize='large' color='primary' />
                    </IconButton>
                    <TableContainer>
                        <Table stickyHeader>
                            <CustomTableHeader />
                            <CustomTableBody
                                data={pagedData}
                                openEditFormDialog={handleOpenEditFormDialog}
                            />
                        </Table>
                    </TableContainer>
                    <CustomPagination
                        dataLength={data?.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </Paper>
                <DialogComponent open={formOpen} onClose={handleFormClose}>
                    <PeerGroupForm
                        record={recordToCreateUpdate}
                        onClose={handleFormClose}
                        onSave={handleFormSave}
                        isCreate={isCreate}
                    />
                </DialogComponent>
            </Box>
        </WithSpinner>
    );
};
